<script setup>
  import {ref} from "vue";

  import ComponentAccordion from "@components/Accordion/Accordion.vue";
  import ComponentAccordionItem from "@components/Accordion/AccordionItem.vue";
  import ComponentCategoryBadge from "@components/Badges/CategoryBadge.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import PatientinputComponentAutocomplete from "@pages/Patientinput/Components/Autocomplete.vue";
  import PatientinputNonUnselectableCheckbox from "@pages/Patientinput/Components/NonUnselectableCheckbox.vue";
  import {symptoms} from "@pages/Patientinput/Enums.js";
  import {MedButton} from "@pharma4u/medicheck-components";
  import {coreRouter} from "@utils/coreRouter/coreRouter.js";

  defineOptions({
    name: "PatientinputAdditionalSymptoms",
  });

  const props = defineProps({
    form: {
      type: Object,
      required: true,
    },
  });

  const refConfirmDeleteDialog = ref(null);

  const lastImportedSymptom = ref(null);
  const symptomImportedTo = ref(null);
  const symptomToDelete = ref(null);

  const fixedSymptomMivs = new Set(symptoms.flatMap((group) => group.items.map((item) => item.miv)));

  const doSearch = (searchTerm) => {
    symptomImportedTo.value = null;
    lastImportedSymptom.value = null;

    return new Promise((resolve) => {
      coreRouter.searchSymptomsForPatientinput(searchTerm, props.form.masterdata.patient.gender, {
        onSuccess: (response) => {
          resolve(response.data);
        },
        onError: () => {
          resolve(false);
        },
      });
    });
  };

  const symptomSelected = (symptom) => {
    if (!props.form.symptoms.mivs.includes(symptom.Key_MIV)) {
      props.form.symptoms.mivs.push(symptom.Key_MIV);

      lastImportedSymptom.value = symptom;

      if (fixedSymptomMivs.has(symptom.Key_MIV)) {
        symptomImportedTo.value = symptoms.find((group) =>
          group.items.some(
            (item) => symptom.Key_MIV === item.miv || (Array.isArray(item.miv) && item.miv.includes(symptom.Key_MIV)),
          ),
        ).title;
      } else {
        props.form.symptoms.list.push(symptom);
        symptomImportedTo.value = "weitere Probleme/Symptome";
      }

      setTimeout(() => {
        symptomImportedTo.value = null;
        lastImportedSymptom.value = null;
      }, 5000);
    }
  };

  const confirmDelete = (disease) => {
    symptomToDelete.value = disease;
    refConfirmDeleteDialog.value.open();
  };

  const deleteConfirmed = () => {
    props.form.symptoms.mivs = props.form.symptoms.mivs.filter((miv) => miv !== symptomToDelete.value.Key_MIV);
    props.form.symptoms.list = props.form.symptoms.list.filter(
      ({Key_MIV}) => Key_MIV !== symptomToDelete.value.Key_MIV,
    );
    symptomToDelete.value = null;
  };
</script>

<template>
  <PatientinputComponentAutocomplete
    placeholder="Bitte Symptom eingeben"
    :fetch-method="doSearch"
    :test-id="getTestId('additional')"
    @selected="symptomSelected"
  >
    <template #autocomplete-item="{item}">
      <div class="flex min-h-7 flex-col justify-center">
        <span>{{ item.Name }}</span>

        <div v-if="form.symptoms.mivs.some((miv) => miv === item.Key_MIV)" class="text-sm text-mcred-500">
          Problem/Symptom wurde bereits hinterlegt
        </div>
      </div>
    </template>

    <template #after-search>
      <div v-if="lastImportedSymptom && symptomImportedTo" class="mt-3 text-sm text-mcgreen">
        „{{ lastImportedSymptom.Name }}“ wurde erfolgreich unter „{{ symptomImportedTo }}“ gespeichert
      </div>
    </template>
  </PatientinputComponentAutocomplete>

  <ComponentAccordion v-if="form.symptoms.list.length > 0">
    <ComponentAccordionItem :test-id="getTestId('additional-symptoms-accordion')">
      <template #title>
        <div class="flex items-center space-x-2">
          <h3>Weitere Probleme/Symptome</h3>
          <sup>
            <ComponentCategoryBadge color="info" :label="form.symptoms.list.length.toString()" />
          </sup>
        </div>
      </template>

      <div class="flex flex-col gap-3">
        <div v-for="(symptom, index) in form.symptoms.list" :key="index" class="flex items-center justify-between">
          <PatientinputNonUnselectableCheckbox
            color="primary"
            large
            :test-id="getTestId('additional-symptoms-delete')"
            @click="confirmDelete(symptom)"
          >
            <span>{{ symptom.Name }}</span>
          </PatientinputNonUnselectableCheckbox>

          <div>
            <MedButton
              :aria-label="symptom.name ? `Symptom ${symptom.name} löschen` : 'Symptom löschen'"
              flat
              icon="delete"
              rounded="full"
              size="sm"
              :data-test="getTestId('additional-symptoms-confirm-delete-button')"
              @click="confirmDelete(symptom)"
            />
          </div>
        </div>
      </div>
    </ComponentAccordionItem>
  </ComponentAccordion>

  <ComponentDialog
    ref="refConfirmDeleteDialog"
    dialog-title="Symptom löschen?"
    primary-label="Ja, Löschen"
    :test-id="getTestId('additional-symptoms-confirm-delete')"
    @primary-button-clicked="deleteConfirmed"
  >
    <template #content>Sind Sie sicher, dass Sie „{{ symptomToDelete.Name }}“ löschen möchten?</template>
  </ComponentDialog>
</template>
